import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Mic, MicOff, Send, Sparkles, X } from "lucide-react";
import { scrollToId } from "./Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = ["C'est quoi Giulia ?", "Combien ça coûte ?", "Comment vous gérez mes données ?"];

const GREETING =
  "Bonjour ! Moi c'est Giulia, l'IA opérateur de GiulIATeck. Demandez-moi ce que je peux faire pour votre entreprise : veille, contenus, pubs, rendez-vous, tout y est.";

export const GiuliaChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const sessionId = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/giulia/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.session_id) sessionId.current = evt.session_id;
            if (evt.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + evt.delta };
                return c;
              });
            }
          } catch (e) {
            /* ignore malformed chunk */
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        if (c[c.length - 1] && c[c.length - 1].role === "assistant" && !c[c.length - 1].content) {
          c[c.length - 1] = { role: "assistant", content: "Oups, je suis momentanément indisponible. Réessayez dans un instant." };
        }
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  const startRec = async () => {
    if (recording || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      recRef.current.ondataavailable = (e) => chunksRef.current.push(e.data);
      recRef.current.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "audio.webm");
          const { data } = await axios.post(`${API}/voice/listen`, fd);
          if (data.text) {
            setInput(data.text);
          } else {
            toast.error("Je n'ai rien entendu. Réessayez.");
          }
        } catch (e) {
          toast.error("La transcription est momentanément indisponible.");
        }
        setTranscribing(false);
      };
      recRef.current.start();
      setRecording(true);
    } catch (e) {
      toast.error("Micro non autorisé.");
    }
  };

  const stopRec = () => {
    recRef.current?.stop();
    setRecording(false);
  };

  const goDemo = () => {
    setOpen(false);
    setTimeout(() => scrollToId("demo"), 150);
  };

  return (
    <>
      <button
        data-testid="giulia-chat-open"
        onClick={() => setOpen(!open)}
        aria-label="Parler à Giulia"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-iris-deep to-viol text-white shadow-[0_0_32px_rgba(139,92,246,0.5)] transition-all duration-300 hover:scale-110"
      >
        {open ? <X size={24} /> : <Sparkles size={24} />}
        {!open && <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-ink" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="giulia-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-50 flex h-[540px] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-3xl glass shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-center gap-3 border-b border-ink-line bg-ink-surface/80 px-4 py-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-iris-deep to-viol font-display font-bold text-sm text-white">G</span>
              <div className="flex-1">
                <p className="font-display font-bold text-sm text-white leading-none">Giulia</p>
                <p className="mt-1 flex items-center gap-1.5 text-[11px] text-mist">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  En ligne — réponse immédiate
                </p>
              </div>
              <button
                data-testid="giulia-chat-cta"
                onClick={goDemo}
                className="rounded-full bg-gradient-to-r from-iris-deep to-viol px-3.5 py-1.5 text-[11px] font-medium text-white"
              >
                Demander une démo
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="giulia-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid="giulia-chat-msg">
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-gradient-to-br from-iris-deep to-viol text-white"
                        : "rounded-bl-sm border border-ink-line bg-ink-surface/90 text-white/90"
                    }`}
                  >
                    {m.content || (
                      <span className="inline-flex items-center gap-2 text-mist">
                        <Loader2 size={13} className="animate-spin" />
                        Giulia écrit…
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-ink-line bg-ink-surface/80 px-4 py-3">
              <div className="mb-2.5 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="giulia-chat-suggestion"
                    onClick={() => send(s)}
                    disabled={streaming}
                    className="rounded-full border border-ink-line px-3 py-1 text-[11px] text-mist transition-colors hover:border-viol/50 hover:text-viol-pale disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="giulia-chat-mic"
                  onClick={recording ? stopRec : startRec}
                  disabled={transcribing || streaming}
                  aria-label={recording ? "Arrêter l'enregistrement" : "Parler à Giulia"}
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-all ${
                    recording
                      ? "border-red-400/60 bg-red-500/15 text-red-300 animate-pulse"
                      : "border-ink-line text-mist hover:border-viol/50 hover:text-viol-pale"
                  }`}
                >
                  {transcribing ? <Loader2 size={17} className="animate-spin" /> : recording ? <MicOff size={17} /> : <Mic size={17} />}
                </button>
                <input
                  data-testid="giulia-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Écrivez à Giulia…"
                  maxLength={500}
                  className="h-10 flex-1 rounded-xl border border-ink-line bg-ink-soft/80 px-3.5 text-sm text-white placeholder:text-mist/50 outline-none focus:border-viol/60 focus:ring-2 focus:ring-viol/25"
                />
                <button
                  data-testid="giulia-chat-send"
                  onClick={() => send()}
                  disabled={streaming || !input.trim()}
                  aria-label="Envoyer"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-iris-deep to-viol text-white transition-all hover:scale-105 disabled:opacity-40"
                >
                  {streaming ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                </button>
              </div>
              <p className="mt-2 text-center text-[10px] text-mist/50">
                Giulia est une IA — ses réponses sont générées automatiquement.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GiuliaChat;
